// Copyright 2020 Sorama B.V.

import { bindable, bindingMode } from "aurelia-framework";
import { IPlotParent, Range } from "controls/plot/IPlotParent";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { Unit } from "units/Unit";
import { PlotPartBase } from "../PlotPartBase";

/**
 * Part that visualizes an indicator
 */
export class RecommendationPart extends PlotPartBase {
    //#region Bindables
    @bindable({ defaultBindingMode: bindingMode.oneWay })
    public mode = "x";

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public recommendedRanges: Range[];
    //#endregion

    static instanceCounter = 0;
    private instanceNumber: number;

    private svgElement: SVGElement;

    public recommendedSections: Rect[] = [];

    public height = 6;

    constructor() {
        super(0);
        this.instanceNumber = RecommendationPart.instanceCounter++;
    }

    public bind(context: IPlotParent): void {
        super.bind(context);
    }

    public attached(): void {
        super.attached();
        this.calculateRecommendations();
    }

    public detached(): void {
        super.detached();
    }

    public onSize(size: Size, margin: Margin): void {
        super.onSize(size, margin);
        this.calculateRecommendations();
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>) {
        super.onViewPortComputed(viewPort, viewPortBounds, units);
        this.calculateRecommendations();
    }

    //#region Changed Handler
    private modeChanged(): void {
        this.calculateRecommendations();
    }
    private recommendedRangesChanged() {
        this.calculateRecommendations();
    }

    private calculateRecommendations(): void {
        if (!this.recommendedRanges || !this.sourceUnits) {
            this.recommendedSections = [];
            return;
        }

        const newRanges: Rect[] = [];

        for (const range of this.recommendedRanges) {
            let from, to;
            if (this.mode == "x") {
                from = this.dataToSvgX(range.from.toUnit(this.sourceUnits.x));
                to = this.dataToSvgX(range.to.toUnit(this.sourceUnits.x));
                newRanges.push(new Rect(from, 0, to - from, this.height));
            } else {
                from = this.dataToSvgY(range.from.toUnit(this.sourceUnits.y));
                to = this.dataToSvgY(range.to.toUnit(this.sourceUnits.y));
                newRanges.push(new Rect(this.actualSize.width - this.height, to, this.height, from - to));
            }
        }

        this.recommendedSections.splice(0, this.recommendedSections.length, ...newRanges);
    }

    private dataToSvgX(x: number): number {
        if (!this.viewPort.width) return 0;
        return ((x - this.viewPort.x) / this.viewPort.width) * this.actualSize.width;
    }

    private dataToSvgY(y: number): number {
        if (!this.viewPort.height) return 0;
        return (1 - (y - this.viewPort.y) / this.viewPort.height) * this.actualSize.height;
    }
}
